import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, X, Calendar } from 'lucide-react';
import axios from 'axios';
import { userInputToUTC } from '../utils/timezone';
import { formatInTimeZone } from 'date-fns-tz';
import TimezoneDisplay from '../components/TimezoneDisplay';

const EditEvent = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storytellers, setStorytellers] = useState([]);
  const [selectedStorytellers, setSelectedStorytellers] = useState([]);
  const [, setEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    timezone: 'America/New_York',
    duration: 3, // Default 3 hours
    location: '',
    createDiscordEvent: false
  });

  // Helper function to get next occurrence of a specific day of week
  const getNextOccurrence = (dayOfWeek) => {
    const today = new Date();
    const targetDay = dayOfWeek; // 0 = Sunday, 3 = Wednesday, 5 = Friday, 6 = Saturday
    const currentDay = today.getDay();
    
    let daysUntilTarget = targetDay - currentDay;
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7; // Next week
    }
    
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilTarget);
    return nextDate;
  };

  // Quick select handlers
  const handleQuickSelect = (dayOfWeek, dayName) => {
    const nextDate = getNextOccurrence(dayOfWeek);
    
    // Create date string in user's local timezone WITHOUT converting to UTC
    const year = nextDate.getFullYear();
    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const day = String(nextDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Generate title if not already set
    const generatedTitle = formData.title || `${dayName} Blood on the Clocktower`;
    
    setFormData(prev => ({
      ...prev,
      date: dateStr,
      time: '20:30', // 8:30 PM
      timezone: 'America/New_York',
      title: generatedTitle
    }));
  };

  // Handle today selection
  const handleTodaySelect = () => {
    const today = new Date();
    
    // Create date string in user's local timezone WITHOUT converting to UTC
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });
    
    // Generate title if not already set
    const generatedTitle = formData.title || `${dayName} Blood on the Clocktower`;
    
    setFormData(prev => ({
      ...prev,
      date: dateStr,
      time: '20:30', // 8:30 PM
      timezone: 'America/New_York',
      title: generatedTitle
    }));
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dayOfWeek) => {
    const nextDate = getNextOccurrence(dayOfWeek);
    return nextDate.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  useEffect(() => {
    const fetchEventAndStorytellers = async () => {
      try {
        // Fetch event details
        const eventResponse = await axios.get(`/api/events/${eventId}`);
        const eventData = eventResponse.data;
        setEvent(eventData);

        // Parse date and time - events are stored in UTC, display in Eastern Time
        const eventDate = new Date(eventData.date);
        
        // Convert UTC to Eastern Time for display (default timezone for the app)
        const localDate = formatInTimeZone(eventDate, 'America/New_York', 'yyyy-MM-dd');
        const localTime = formatInTimeZone(eventDate, 'America/New_York', 'HH:mm');

        setFormData({
          title: eventData.title || '',
          description: eventData.description || '',
          date: localDate,
          time: localTime,
          timezone: 'America/New_York', // Default to Eastern Time since events are stored in UTC
          duration: Math.round((eventData.duration || 180) / 60 * 10) / 10, // Convert minutes to hours
          location: eventData.location || '',
          createDiscordEvent: false
        });

        // Set selected storytellers
        setSelectedStorytellers(eventData.storytellers?.map(s => s.id) || []);

        // Fetch available storytellers
        const storytellersResponse = await axios.get('/api/users/storytellers/list');
        setStorytellers(storytellersResponse.data);
      } catch (error) {
        console.error('Failed to fetch event or storytellers:', error);
        if (error.response?.status === 404) {
          navigate('/events');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEventAndStorytellers();
  }, [eventId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Extract date and time from form data
      const dateString = formData.date;
      const timeString = formData.time;
      const timezone = formData.timezone;
      
      // Convert to UTC using the event's timezone
      const dateTime = userInputToUTC(dateString, timeString, timezone);
      
      const updateData = {
        title: formData.title,
        description: formData.description,
        date: dateTime,
        duration: parseInt(formData.duration) * 60, // Convert hours to minutes
        location: formData.location,
        storytellerIds: selectedStorytellers
      };

      await axios.put(`/api/events/${eventId}`, updateData);
      
      // Show success message
      alert('Event updated successfully! Discord event has also been updated.');
      
      navigate(`/events/${eventId}`);
    } catch (error) {
      console.error('Failed to update event:', error);
      alert('Failed to update event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newFormData = { 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      };
      
      // Auto-generate title when date/time is set and title is empty
      if ((name === 'date' || name === 'time') && !prev.title) {
        if (newFormData.date && newFormData.time) {
          try {
            const eventDate = new Date(`${newFormData.date}T${newFormData.time}`);
            const dayName = eventDate.toLocaleDateString('en-US', { weekday: 'long' });
            newFormData.title = `${dayName} Blood on the Clocktower`;
          } catch (error) {
            console.error('Error generating title:', error);
          }
        }
      }
      
      return newFormData;
    });
  };

  const handleStorytellerToggle = (storytellerId) => {
    setSelectedStorytellers(prev => 
      prev.includes(storytellerId)
        ? prev.filter(id => id !== storytellerId)
        : [...prev, storytellerId]
    );
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading event...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Event</h1>
          <button
            onClick={() => navigate(`/events/${eventId}`)}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <X size={16} />
            <span>Cancel</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Basic Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Event Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="input w-full"
                  placeholder="Enter event title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="input w-full"
                  placeholder="Enter event description"
                />
              </div>
            </div>
          </div>

          {/* Date and Time */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Date & Time</h2>
            
            {/* Quick Select Buttons */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Quick Select (8:30 PM ET)
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleQuickSelect(3, 'Wednesday')}
                  className="px-3 py-2 text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                  title={`Next Wednesday: ${formatDateForDisplay(3)}`}
                >
                  Next Wednesday
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickSelect(5, 'Friday')}
                  className="px-3 py-2 text-sm bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                  title={`Next Friday: ${formatDateForDisplay(5)}`}
                >
                  Next Friday
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickSelect(6, 'Saturday')}
                  className="px-3 py-2 text-sm bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
                  title={`Next Saturday: ${formatDateForDisplay(6)}`}
                >
                  Next Saturday
                </button>
                <button
                  type="button"
                  onClick={handleTodaySelect}
                  className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                >
                  Today
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Hover over buttons to see the exact date
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time *
                </label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  className="input w-full"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timezone *
              </label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                required
                className="input w-full"
              >
                <option value="America/New_York">🇺🇸 America/New_York (Eastern Time) - Default</option>
                <option value="America/Chicago">🇺🇸 America/Chicago (Central Time)</option>
                <option value="America/Denver">🇺🇸 America/Denver (Mountain Time)</option>
                <option value="America/Los_Angeles">🇺🇸 America/Los_Angeles (Pacific Time)</option>
                <option value="America/Phoenix">🇺🇸 America/Phoenix (Arizona Time)</option>
                <option value="America/Anchorage">🇺🇸 America/Anchorage (Alaska Time)</option>
                <option value="America/Honolulu">🇺🇸 America/Honolulu (Hawaii Time)</option>
                <option value="Europe/London">🇬🇧 Europe/London (GMT/BST)</option>
                <option value="Europe/Paris">🇫🇷 Europe/Paris (CET/CEST)</option>
                <option value="Europe/Berlin">🇩🇪 Europe/Berlin (CET/CEST)</option>
                <option value="Asia/Tokyo">🇯🇵 Asia/Tokyo (JST)</option>
                <option value="Asia/Shanghai">🇨🇳 Asia/Shanghai (CST)</option>
                <option value="Asia/Singapore">🇸🇬 Asia/Singapore (SGT)</option>
                <option value="Asia/Dubai">🇦🇪 Asia/Dubai (GST)</option>
                <option value="Australia/Sydney">🇦🇺 Australia/Sydney (AEST/AEDT)</option>
                <option value="Pacific/Auckland">🇳🇿 Pacific/Auckland (NZST/NZDT)</option>
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Events are created in Eastern Time by default. The timezone display below shows how this will appear in different timezones.
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Duration (hours)
              </label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                min="0.5"
                max="8"
                step="0.5"
                className="input w-full"
              />
            </div>
            
            {/* Timezone Display */}
            <TimezoneDisplay 
              date={formData.date} 
              time={formData.time} 
              timezone={formData.timezone} 
            />
          </div>

          {/* Location */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Location</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="input w-full"
                placeholder="Enter event location"
              />
            </div>
          </div>

          {/* Storytellers */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Storytellers</h2>
            
            {storytellers.length > 0 ? (
              <div className="space-y-3">
                {storytellers.map((storyteller) => (
                  <label key={storyteller.id} className="flex items-center space-x-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedStorytellers.includes(storyteller.id)}
                      onChange={() => handleStorytellerToggle(storyteller.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex items-center space-x-3 flex-1">
                      {storyteller.avatar && storyteller.discord_id && (
                        <img
                          src={`https://cdn.discordapp.com/avatars/${storyteller.discord_id}/${storyteller.avatar}.png`}
                          alt={storyteller.username}
                          className="w-8 h-8 rounded-full"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {storyteller.username}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                          {storyteller.role}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No storytellers available</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate(`/events/${eventId}`)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="btn btn-secondary flex items-center space-x-2"
              title="Go to Admin panel to create template events"
            >
              <Calendar size={16} />
              <span>Use Templates</span>
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Save size={16} />
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEvent; 